import { bindSequenceSettings } from "./focus-sequence.js";
import {
  bindMonthlyTable,
  resetTableView,
  currentTableMonth,
  currentTableSlice,
} from "./plan-table-view.js";
import {
  planningGroups,
  plansIn,
  mutablePlans,
  savePlans,
  planTarget,
  profile,
  preservePlanHistory,
} from "../core/planning.js";
import { openPlanningSetup } from "./planning-setup.js";
import {
  bindPlanEdits,
  savePlanEdits,
  showPlanEditError,
} from "./plan-inline.js";
import {
  playCompletionChime,
  primeCompletionAudio,
} from "./completion-sound.js";
import { l } from "../core/i18n.js";
import { validateBackup } from "../../shared/schema.js";
import { resetWorkspace, flush } from "../services/storage.js";
import { render, studySummary } from "./views.js";
import { updateDashboardProgress } from "./dashboard.js";
import { goToday, navigate, setPage, toast } from "../core/navigation.js";
import {
  closeModal,
  deleteExam,
  deletePlan,
  openExamModal,
  openPlanModal,
} from "./plans.js";
import {
  compFor,
  dateKey,
  ensurePlanGroups,
  ensureStudyCategories,
  ensureStudyFlow,
  esc,
  fromKey,
  persist,
  planGroup,
  studyItemsForToday,
  tasksFor,
  upgradeWeekendPlan,
  upgradeWorkweekPlan,
} from "../core/model.js";
import { saveJSON } from "../core/storage.js";
import { KEYS } from "../../shared/schema.js";
import {
  pausePom,
  resetPom,
  saveActiveTimers,
  skipPom,
  startPom,
  startStudyFlow,
  syncPom,
  tickStudy,
  updateTimerDOM,
} from "./timers.js";
import { runtime } from "../core/runtime.js";
import { openFloatingTimer, toggleTimerVisibility } from "./floating-timer.js";

export function bindPage() {
  bindPlanEdits(() => {
    const invalid = savePlanEdits();
    if (invalid) {
      runtime.manageDay = invalid.group;
      if (invalid.group.startsWith("date:"))
        runtime.selectedDate = fromKey(invalid.group.slice(5));
      render();
      showPlanEditError(invalid.id);
      return;
    }
    render();
  });
  document
    .querySelectorAll("[data-planning-setup]")
    .forEach(
      (button) =>
        (button.onclick = () =>
          openPlanningSetup(
            button.dataset.planningTarget
              ? { week: button.dataset.planningTarget, step: 1 }
              : {},
          )),
    );
  document
    .querySelectorAll("[data-edit-week]")
    .forEach(
      (button) =>
        (button.onclick = () =>
          openPlanningSetup({ week: button.dataset.editWeek, step: 1 })),
    );
  document.querySelectorAll("[data-preview-chime]").forEach(
    (button) =>
      (button.onclick = () => {
        void playCompletionChime(true);
      }),
  );
  document.querySelectorAll("[data-planning-week-shift]").forEach(
    (button) =>
      (button.onclick = () => {
        runtime.selectedDate.setDate(
          runtime.selectedDate.getDate() +
            Number(button.dataset.planningWeekShift) * 7,
        );
        runtime.manageDay = planningGroups()[0].id;
        render();
      }),
  );
  document
    .querySelector("[data-quick-plan]")
    ?.addEventListener("submit", (event) => {
      event.preventDefault();
      const form = event.currentTarget,
        name = form.elements.name.value.trim();
      if (!name) return;
      preservePlanHistory();
      const tasks = mutablePlans(runtime.manageDay);
      if (tasks.length >= 1000) return;
      tasks.push({
        id: "plan_" + crypto.randomUUID(),
        name,
        schedule: form.elements.schedule.value.trim(),
        notes: "",
        study: false,
      });
      savePlans(runtime.manageDay);
      saveJSON(KEYS.custom, runtime.state.custom);
      render();
      document
        .querySelector('[data-quick-plan] [name="name"]')
        ?.focus({ preventScroll: true });
    });
  document
    .querySelector("[data-floating-timer]")
    ?.addEventListener("click", openFloatingTimer);
  document
    .querySelector("[data-timer-visibility]")
    ?.addEventListener("click", toggleTimerVisibility);
  document.querySelectorAll("[data-page]").forEach(
    (b) =>
      (b.onclick = () => {
        if (b.dataset.page === "table") {
          resetTableView();
        }
        runtime.page = b.dataset.page;
        runtime.menuOpen = false;
        document.body.classList.remove("menu-open");
        document
          .getElementById("menuBtn")
          .setAttribute("aria-expanded", "false");
        render();
      }),
  );
  document
    .querySelectorAll("[data-open-exams]")
    .forEach((b) => (b.onclick = () => setPage("exams")));
  document
    .querySelectorAll("[data-add-exam]")
    .forEach((b) => (b.onclick = () => openExamModal()));
  document.querySelectorAll("[data-edit-exam]").forEach(
    (b) =>
      (b.onclick = () => {
        const exam = runtime.state.exams.find(
          (x) => x.id === b.dataset.editExam,
        );
        if (exam) openExamModal(exam);
      }),
  );
  document.querySelectorAll("[data-delete-exam]").forEach(
    (b) =>
      (b.onclick = () => {
        const exam = runtime.state.exams.find(
          (x) => x.id === b.dataset.deleteExam,
        );
        if (exam) deleteExam(exam);
      }),
  );
  document.querySelectorAll("[data-open-date]").forEach(
    (b) =>
      (b.onclick = () => {
        runtime.selectedDate = fromKey(b.dataset.openDate);
        runtime.page = "today";
        render();
      }),
  );
  document.querySelectorAll("[data-manage-day]").forEach(
    (b) =>
      (b.onclick = () => {
        runtime.manageDay = b.dataset.manageDay;
        render();
      }),
  );
  document.querySelectorAll("[data-edit-group]").forEach(
    (b) =>
      (b.onclick = () => {
        runtime.manageDay = b.dataset.editGroup;
        runtime.page = "manage";
        render();
        window.scrollTo(0, 0);
      }),
  );
  bindMonthlyTable(render);
  document
    .querySelectorAll("[data-complete-date]")
    .forEach((ch) => (ch.onchange = () => toggleTableCell(ch)));
  document.querySelectorAll(".sheet-cell").forEach(
    (cell) =>
      (cell.onclick = (e) => {
        if (e.target.matches("input")) return;
        const ch = cell.querySelector("input");
        if (ch && !ch.disabled) ch.click();
      }),
  );
  document.querySelectorAll(".task-check").forEach(
    (ch) =>
      (ch.onchange = () => {
        const row = ch.closest(".task"),
          id = row.dataset.taskId,
          k = dateKey(runtime.selectedDate);
        if (k > dateKey(new Date())) {
          ch.checked = false;
          return;
        }
        compFor(k)[id] = ch.checked;
        saveJSON(KEYS.completions, runtime.state.completions);
        // On the dashboard the row changes in place so its strike-through draws.
        if (row.closest(".kun-dash")) {
          row.classList.toggle("done", ch.checked);
          updateDashboardProgress();
          return;
        }
        render();
        document
          .querySelector(`.task[data-task-id="${CSS.escape(id)}"] .task-check`)
          ?.focus({ preventScroll: true });
      }),
  );
  document.querySelectorAll("[data-edit-task]").forEach(
    (b) =>
      (b.onclick = () => {
        const day =
            runtime.page === "manage"
              ? runtime.manageDay
              : planTarget(runtime.selectedDate),
          t = plansIn(day).find((x) => x.id === b.dataset.editTask);
        if (t) openPlanModal(t, day);
      }),
  );
  document.querySelectorAll("[data-delete-task]").forEach(
    (b) =>
      (b.onclick = () => {
        const day =
            runtime.page === "manage"
              ? runtime.manageDay
              : planTarget(runtime.selectedDate),
          t = plansIn(day).find((x) => x.id === b.dataset.deleteTask);
        if (t) deletePlan(t, day);
      }),
  );
  document
    .querySelectorAll("[data-up]")
    .forEach((b) => (b.onclick = () => moveTask(b.dataset.up, -1)));
  document
    .querySelectorAll("[data-down]")
    .forEach((b) => (b.onclick = () => moveTask(b.dataset.down, 1)));
  document
    .querySelectorAll("[data-action=add]")
    .forEach((b) => (b.onclick = () => openPlanModal()));
  document
    .querySelectorAll("[data-create-plan]")
    .forEach(
      (button) =>
        (button.onclick = () => openPlanModal(null, button.dataset.createPlan)),
    );
  document.querySelectorAll("[data-action=pom],[data-action=pomStart]").forEach(
    (b) =>
      (b.onclick = () => {
        if (b.dataset.action === "pomStart") {
          runtime.focusTimer?.running ? pausePom() : startPom();
        } else {
          runtime.page = "pomodoro";
          startPom();
        }
      }),
  );
  document.querySelectorAll("[data-pom]").forEach((b) => {
    b.onclick = () => {
      if (b.dataset.pom === "start") startPom();
      if (b.dataset.pom === "pause") pausePom();
      if (b.dataset.pom === "reset") resetPom();
      if (b.dataset.pom === "skip") skipPom();
    };
  });
  document
    .querySelector("[data-focus-task]")
    ?.addEventListener("change", (e) => {
      runtime.state.pom.selectedTask = e.target.value;
      persist();
    });
  document
    .querySelectorAll("[data-pref-check]")
    .forEach((box) => (box.onchange = () => togglePref(box.dataset.prefCheck)));
  document
    .querySelector("[data-study-start]")
    ?.addEventListener("click", startStudyFlow);
  document.querySelectorAll("[data-study-enabled]").forEach(
    (ch) =>
      (ch.onchange = () => {
        const group = planGroup(new Date()),
          id = ch.dataset.studyEnabled,
          minutes =
            Number(
              document.querySelector(`[data-study-minutes="${id}"]`)?.value,
            ) || 25;
        (runtime.state.prefs.studyFlow[group] ||= {})[id] = {
          enabled: ch.checked,
          minutes: Math.max(1, Math.min(600, minutes)),
        };
        saveJSON(KEYS.prefs, runtime.state.prefs);
        updateStudySummary();
      }),
  );
  document.querySelectorAll("[data-study-minutes]").forEach(
    (inp) =>
      (inp.onchange = () => {
        const group = planGroup(new Date()),
          id = inp.dataset.studyMinutes,
          minutes = Math.max(1, Math.min(600, Number(inp.value) || 25));
        inp.value = minutes;
        (runtime.state.prefs.studyFlow[group] ||= {})[id] = {
          enabled: !!document.querySelector(`[data-study-enabled="${id}"]`)
            ?.checked,
          minutes,
        };
        saveJSON(KEYS.prefs, runtime.state.prefs);
        updateStudySummary();
      }),
  );
  document
    .querySelectorAll("[data-toggle]")
    .forEach((b) => (b.onclick = () => togglePref(b.dataset.toggle)));
  document
    .querySelectorAll("[data-setting-toggle]")
    .forEach((b) => (b.onclick = () => togglePref(b.dataset.settingToggle)));
  bindSequenceSettings();
  document
    .querySelectorAll("[data-data]")
    .forEach((b) => (b.onclick = () => dataAction(b.dataset.data)));
  document
    .querySelector("[data-focus-select]")
    ?.addEventListener("click", openFocusSelect);
  document.querySelectorAll("[data-drag]").forEach(bindPlanDrag);
}

export function toggleTableCell(input) {
  const key = input.dataset.completeDate,
    id = input.dataset.completeTask;
  if (key > dateKey(new Date())) {
    input.checked = false;
    return;
  }
  const date = fromKey(key),
    task = tasksFor(date).find((t) => t.id === id);
  if (!task) {
    input.checked = false;
    return;
  }
  const checks = compFor(key),
    done = input.checked;
  if (done) checks[id] = true;
  else delete checks[id];
  saveJSON(KEYS.completions, runtime.state.completions);
  const kind = done ? "done" : key < dateKey(new Date()) ? "missed" : "today";
  const label = done
    ? l("Completed")
    : kind === "missed"
      ? l("Not completed")
      : l("Mark completed");
  input.closest("td").className =
    `sheet-cell ${kind} ${task.study ? "study-column" : ""}`;
  input.setAttribute("aria-label", `${task.name} — ${key}: ${label}`);
  const progress = input
    .closest(".month-sheet")
    ?.querySelector(`[data-date-progress="${key}"]`);
  if (progress) {
    const inputs = [
      ...input
        .closest(".month-sheet")
        .querySelectorAll(`[data-complete-date="${key}"]`),
    ];
    progress.textContent = `${inputs.filter((check) => check.checked).length}/${inputs.length}`;
  }
}

export function moveTask(id, dir) {
  const a = mutablePlans(runtime.manageDay),
    i = a.findIndex((x) => x.id === id),
    j = i + dir;
  if (i < 0 || j < 0 || j >= a.length) return;
  [a[i], a[j]] = [a[j], a[i]];
  savePlans(runtime.manageDay);
  render();
}

export function reorderDrag(from, to, before) {
  if (!from || !to || from === to) return;
  const a = mutablePlans(runtime.manageDay),
    i = a.findIndex((x) => x.id === from),
    j = a.findIndex((x) => x.id === to);
  if (i < 0 || j < 0) return;
  let dest = j + (before ? 0 : 1);
  if (i < dest) dest--;
  if (i === dest) return;
  const [task] = a.splice(i, 1);
  a.splice(dest, 0, task);
  savePlans(runtime.manageDay);
  render();
}

export function bindPlanDrag(row) {
  let active = null;
  row.querySelector(".drag-handle").onkeydown = (event) => {
    if (!["ArrowUp", "ArrowDown"].includes(event.key)) return;
    event.preventDefault();
    moveTask(row.dataset.drag, event.key === "ArrowUp" ? -1 : 1);
    document
      .querySelector(
        `[data-drag="${CSS.escape(row.dataset.drag)}"] .drag-handle`,
      )
      ?.focus({ preventScroll: true });
  };
  function markTarget() {
    document
      .querySelectorAll(".manage-item.drop-before,.manage-item.drop-after")
      .forEach((el) => el.classList.remove("drop-before", "drop-after"));
    active.target = null;
    const target = document
      .elementFromPoint(active.x, active.y)
      ?.closest(".manage-item");
    if (!target || target === row) return;
    active.target = target;
    active.before =
      active.y < target.getBoundingClientRect().top + target.offsetHeight / 2;
    target.classList.add(active.before ? "drop-before" : "drop-after");
  }
  row.onpointerdown = (e) => {
    if (
      e.button !== 0 ||
      e.target.closest(".manage-actions, input, textarea, select, a") ||
      (e.target.closest("button") && !e.target.closest(".drag-handle")) ||
      (e.pointerType === "touch" && !e.target.closest(".drag-handle"))
    )
      return;
    active = {
      id: e.pointerId,
      x: e.clientX,
      y: e.clientY,
      startX: e.clientX,
      startY: e.clientY,
      dragging: false,
      target: null,
      before: false,
      frame: 0,
    };
    row.setPointerCapture(e.pointerId);
  };
  row.onpointermove = (e) => {
    if (!active || e.pointerId !== active.id) return;
    active.x = e.clientX;
    active.y = e.clientY;
    if (
      !active.dragging &&
      Math.hypot(active.x - active.startX, active.y - active.startY) < 5
    )
      return;
    if (!active.dragging) {
      active.dragging = true;
      row.classList.add("dragging");
    }
    markTarget();
    if (!active.frame) {
      const scroll = () => {
        if (!active?.dragging) return;
        const edge = 65,
          delta =
            active.y < edge
              ? -Math.min(15, (edge - active.y) / 4)
              : active.y > innerHeight - edge
                ? Math.min(15, (active.y - innerHeight + edge) / 4)
                : 0;
        if (delta) {
          window.scrollBy(0, delta);
          markTarget();
        }
        active.frame = requestAnimationFrame(scroll);
      };
      active.frame = requestAnimationFrame(scroll);
    }
  };
  const finish = (e) => {
    if (!active || e.pointerId !== active.id) return;
    const drag = active;
    if (drag.frame) cancelAnimationFrame(drag.frame);
    row.classList.remove("dragging");
    document
      .querySelectorAll(".manage-item.drop-before,.manage-item.drop-after")
      .forEach((el) => el.classList.remove("drop-before", "drop-after"));
    active = null;
    if (e.type === "pointerup" && drag.dragging && drag.target)
      reorderDrag(row.dataset.drag, drag.target.dataset.drag, drag.before);
  };
  row.onpointerup = finish;
  row.onpointercancel = finish;
}

function updateStudySummary() {
  const summary = document.querySelector("[data-study-summary]");
  if (summary) summary.textContent = studySummary();
  const start = document.querySelector("[data-study-start]");
  if (start) start.disabled = !studyItemsForToday().length;
}

export function togglePref(key) {
  if (key === "sound") {
    runtime.state.prefs.sound = !runtime.state.prefs.sound;
    runtime.state.pom.sound = runtime.state.prefs.sound;
    if (runtime.state.prefs.sound) primeCompletionAudio();
  }
  if (key === "loop") {
    runtime.state.prefs.loop = !runtime.state.prefs.loop;
    runtime.state.pom.loop = runtime.state.prefs.loop;
  }
  if (key === "notifications") {
    runtime.state.prefs.notifications = !runtime.state.prefs.notifications;
    runtime.state.pom.notifications = runtime.state.prefs.notifications;
    if (runtime.state.prefs.notifications && "Notification" in window)
      Notification.requestPermission().catch(() => {});
  }
  persist();
  render();
}

export function openFocusSelect() {
  const ts = tasksFor(runtime.selectedDate);
  document.getElementById("modal").innerHTML =
    `${l('<div class="modal"><h2>Focus on task</h2><label>Today\'s task</label><select id="focusTask" class="field"><option value="">No selected task</option>')}${ts.map((t) => `<option value="${esc(t.name)}">${esc(t.name)}</option>`).join("")}${l('</select><div class="modal-actions"><button class="btn" data-close>Cancel</button><button class="btn black" data-save-focus>Save</button></div></div>')}`;
  document.getElementById("modal").classList.add("show");
  document.getElementById("focusTask").value =
    runtime.state.pom.selectedTask || "";
  document.querySelector("[data-save-focus]").onclick = () => {
    runtime.state.pom.selectedTask = document.getElementById("focusTask").value;
    persist();
    closeModal();
    render();
  };
}

export async function dataAction(type) {
  if (type === "export") {
    const payload = {
      version: 2,
      exportedAt: new Date().toISOString(),
      templates: runtime.state.templates,
      completions: runtime.state.completions,
      custom: runtime.state.custom,
      pom: runtime.state.pom,
      stats: runtime.state.stats,
      prefs: runtime.state.prefs,
      exams: runtime.state.exams,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
        type: "application/json",
      }),
      a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "dayx-backup.json";
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    toast("Data exported.");
  } else if (type === "import") document.getElementById("importFile").click();
  else if (type === "reset") {
    if (confirm(l("Reset all DayX data? This cannot be undone."))) {
      try {
        await resetWorkspace();
        runtime.skipUnloadSave = true;
        clearInterval(startPom.iv);
        clearInterval(runtime.studyInterval);
        location.reload();
      } catch (error) {
        toast(error.message);
      }
    }
  }
}

export function syncTimersAfterReturn() {
  if (runtime.focusTimer?.running) {
    syncPom();
    updateTimerDOM();
  }
  if (runtime.studyTimer?.running) tickStudy();
}

export function refreshCalendarDay() {
  const today = dateKey(new Date());
  if (today === runtime.renderedDay) return;
  if (dateKey(runtime.selectedDate) === runtime.renderedDay)
    runtime.selectedDate = fromKey(today);
  if (
    runtime.tableMonth === runtime.renderedDay.slice(0, 7) + "-01" ||
    !runtime.tableMonth
  ) {
    runtime.tableMonth = currentTableMonth();
    runtime.tableSlice = currentTableSlice();
    runtime.tableScrollTarget = "recent";
  }
  runtime.renderedDay = today;
  preservePlanHistory();
  render();
}

export function bindShell() {
  document.getElementById("importFile").onchange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      if (file.size > 4 * 1024 * 1024) throw Error();
      const x = validateBackup(JSON.parse(await file.text()));
      if (confirm(l("Replace existing data with this backup?"))) {
        clearInterval(startPom.iv);
        clearInterval(runtime.studyInterval);
        runtime.focusTimer = null;
        runtime.studyTimer = null;
        saveActiveTimers();
        runtime.state.templates = x.templates;
        runtime.state.completions = x.completions;
        runtime.state.custom = x.custom || {};
        runtime.state.pom = x.pom;
        runtime.state.stats = x.stats;
        runtime.state.prefs = x.prefs;
        runtime.state.exams = Array.isArray(x.exams) ? x.exams : [];
        ensurePlanGroups();
        upgradeWorkweekPlan();
        upgradeWeekendPlan();
        ensureStudyCategories();
        ensureStudyFlow();
        runtime.tableGroup = planningGroups().some(
          (g) => g.id === runtime.state.prefs.tableGroup,
        )
          ? runtime.state.prefs.tableGroup
          : planningGroups()[0].id;
        persist();
        render();
        toast("Data imported successfully.");
      }
    } catch {
      toast("Invalid DayX backup file.");
    }
    e.target.value = "";
  };
  document.getElementById("prevBtn").onclick = () => navigate(-1);
  document.getElementById("nextBtn").onclick = () => navigate(1);
  document.getElementById("todayBtn").onclick = goToday;
  document.getElementById("quickExam").onclick = () => setPage("exams");
  document.getElementById("quickPom").onclick = () => setPage("pomodoro");
  document.getElementById("menuBtn").onclick = () => {
    runtime.menuOpen = !runtime.menuOpen;
    document.body.classList.toggle("menu-open", runtime.menuOpen);
    document
      .getElementById("menuBtn")
      .setAttribute("aria-expanded", String(runtime.menuOpen));
  };
  document.getElementById("modal").addEventListener("click", (e) => {
    if (e.target.id === "modal" || e.target.closest("[data-close]"))
      closeModal();
  });
  document.addEventListener("keydown", (e) => {
    if (
      e.key === "Escape" &&
      document.getElementById("modal").classList.contains("show")
    )
      closeModal();
  });
  window.addEventListener("beforeunload", () => {
    if (!runtime.skipUnloadSave) {
      persist();
      saveActiveTimers();
    }
  });
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) syncTimersAfterReturn();
  });
  setInterval(refreshCalendarDay, 60000);
  window.addEventListener("focus", () => {
    refreshCalendarDay();
    syncTimersAfterReturn();
  });
}
